import React, { useEffect, useState } from 'react'
import DashboardLayout from '../DashboardLayout'
import CustomTabs from '../../CustomTabs'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { trackPromise } from 'react-promise-tracker'
import api from '../../../../api'
import { isSuccess, showError } from '../../../../utils/helper'
import { useProjectPermission } from '../../../../context/ProjectContext'
import FullScreenLoader from '../../FullScreenLoader'

type Props = {
  breadcrumbData?: any
  children: any
}

const ProjectLayout = (props: Props) => {
  const { breadcrumbData, children } = props
  const { projectPermission, setProjectPermission } = useProjectPermission()
  const [tabsData, setTabsData] = useState([])
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const { name, id } = params
  const [defaultTabIndex, setDefaultTabIndex] = useState<number | null>(null)

  const getTMURL = (slug: string) => {
    return `/project-management/${name}/${id}/task-manager/${slug}`
  }

  const tmSlugObjects = [
    {
      label: 'Observations',
      component: children,
      value: getTMURL('observations'),
      slug: 'observations',
    },
    {
      label: 'Inspections',
      component: children,
      value: getTMURL('inspections/schedule'),
      slug: 'inspections',
    },
    {
      label: 'Incidents',
      component: children,
      value: getTMURL('incidents'),
      slug: 'incidents',
    },
    {
      label: 'Trainings',
      component: children,
      value: getTMURL('trainings'),
      slug: 'training',
    },
  ]

  function getFirstAvailableTaskManagerURL(projectSlugs: any) {
    // Find the first tmSlug object that has its slug included in projectSlugs
    const firstAvailableSlugObject = tmSlugObjects.find((item: any) => projectSlugs.includes(item.slug))

    // If any match is found, return the URL from that object's value property
    if (firstAvailableSlugObject) {
      return firstAvailableSlugObject.value // This already contains the getURL result
    }

    // If no match is found, return null or a default URL
    return null
  }

  const getTabs = () => {
    return [
      {
        label: 'User Management',
        component: children,
        value: getURL('user-management/users'),
        slug: 'user',
      },
      {
        label: 'nAI Flow',
        component: children,
        value: getURL('nai-flow/whatsapp-group'),
        slug: 'nai-flow',
      },
      {
        label: 'nAI Hub',
        component: children,
        value: getURL('nai-hub'),
        slug: 'nai-hub',
      },
    ]
  }

  useEffect(() => {
    if (projectPermission) {
      const projectSlugs = projectPermission?.map((p: any) => p?.slug)
      const authTabs = getTabs()?.filter((f: any) => projectSlugs?.includes(f?.slug)) as any

      setTabsData(authTabs)
      const ind = ['details', 'user', 'role', 'task-manager', 'statistics-capture', 'nai-hub']
        .filter((m: any) => {
          if (m === 'task-manager' && projectSlugs?.includes(m)) {
            const tmSlugs = ['observations', 'inspections', 'incidents', 'training']
            return tmSlugs.some((slug) => projectSlugs.includes(slug))
          } else if (m === 'statistics-capture') {
            return projectSlugs?.includes(m) && (projectSlugs?.includes('schedule') || projectSlugs?.includes('report'))
          } else {
            return projectSlugs?.includes(m) || m === 'details' || m === 'copilot'
          }
        })
        ?.findIndex((p: string) => location?.pathname?.split('/')[location?.pathname?.split('/')?.length - 1]?.includes(p))
      setDefaultTabIndex(ind)
    } else {
      trackPromise(
        api
          .getProjectPermissionsByProjectID(id)
          .then((response: any) => {
            if (isSuccess(response)) {
              const d = response?.data?.data
              const arr: any = [...(d?.modules || [])]
              arr.is_admin_user = d?.is_admin_user
              arr.is_project_admin = d?.is_project_admin
              setProjectPermission(arr)
            }
          })
          .catch((error: any) => {
            showError(error)
          }),
      )
    }
  }, [projectPermission])

  const getURL = (slug: string) => {
    return `/project-management/${name}/${id}/${slug}`
  }

  return (
    <DashboardLayout breadcrumbData={breadcrumbData}>
      <div className="grid grid-cols-12 h-full">
        <div className="col-span-12">
          {defaultTabIndex === null ? (
            <FullScreenLoader loading={true} />
          ) : (
            <CustomTabs
              handleClick={(e: string) => navigate(e)}
              tabs={[
                {
                  label: 'Details',
                  component: children,
                  value: getURL('details'),
                },
                ...tabsData,
              ]}
              defaultTabIndex={defaultTabIndex || 0}
            />
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}

export default ProjectLayout
